import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useVersion } from './useVersion'
import { fetchLastSeenRelease, markReleaseSeen } from '../services/releaseApi'
import { hasUnseen } from '../utils/releases'

interface UseLastSeenReleaseResult {
  currentVersion: string | null
  lastSeenVersion: string | null
  unseen: boolean
  loading: boolean
  markSeen: () => Promise<void>
}

// Multiple components consume useLastSeenRelease independently (the alert
// stack and the footer VersionDisplay). Each instance owns its own state, so
// without a broadcast the footer badge wouldn't clear until reload. Mirror
// the SYNC_COMPLETED_EVENT pattern in useSyncFreshness.
export const RELEASE_SEEN_EVENT = 'evop:release-seen'

// Per-user sessionStorage cache of the last-seen version so the release-notes
// alert resolves at first paint (stale-while-revalidate) rather than flipping
// on after the async fetch and shifting the page down - the same anti-reflow
// pattern as useSyncFreshness's cache and useVersion's app_version hydrate. The
// {version} wrapper lets a genuine "never seen anything" (null) be cached
// distinctly from a cache miss, so that case paints the banner immediately too.
const lastSeenCacheKey = (userId: number | string) =>
  `evop_last_seen_release_${userId}`

const readLastSeenCache = (key: string): { version: string | null } | null => {
  try {
    const raw = sessionStorage.getItem(key)
    if (raw === null) return null
    const parsed = JSON.parse(raw)
    return { version: typeof parsed?.version === 'string' ? parsed.version : null }
  } catch {
    return null
  }
}

const writeLastSeenCache = (key: string, version: string | null) => {
  try {
    sessionStorage.setItem(key, JSON.stringify({ version }))
  } catch {}
}

/**
 * Resolves the current bundled version against the server-side `last_seen_release_version`
 * for the authenticated user. Exposes a `markSeen` helper that the dialog/banner call
 * when the user acknowledges the release.
 */
export function useLastSeenRelease(): UseLastSeenReleaseResult {
  const currentVersion = useVersion()
  const { isAuthenticated, user } = useAuth()
  const cacheKey = lastSeenCacheKey(user?.id ?? 'anon')
  const [lastSeenVersion, setLastSeenVersion] = useState<string | null>(
    () => readLastSeenCache(cacheKey)?.version ?? null,
  )
  const [loading, setLoading] = useState<boolean>(
    () => isAuthenticated && readLastSeenCache(cacheKey) === null,
  )

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false)
      return
    }
    let cancelled = false
    // Only gate on loading for a cold cache; a cached value keeps the alert
    // state stable at first paint while we revalidate in the background.
    if (readLastSeenCache(cacheKey) === null) setLoading(true)
    fetchLastSeenRelease()
      .then((data) => {
        if (cancelled) return
        setLastSeenVersion(data.version)
        writeLastSeenCache(cacheKey, data.version)
      })
      .catch(() => {
        // Keep the last-known value (cached seed or prior fetch) on a transient
        // failure rather than clearing it and flashing the banner on/off.
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isAuthenticated, cacheKey])

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ version: string | null }>).detail
      if (detail?.version) {
        setLastSeenVersion(detail.version)
        writeLastSeenCache(cacheKey, detail.version)
      }
    }
    window.addEventListener(RELEASE_SEEN_EVENT, handler)
    return () => window.removeEventListener(RELEASE_SEEN_EVENT, handler)
  }, [cacheKey])

  const markSeen = useCallback(async () => {
    if (!currentVersion || !isAuthenticated) return
    if (lastSeenVersion === currentVersion) return
    try {
      const result = await markReleaseSeen(currentVersion)
      const seenVersion = result.version ?? currentVersion
      setLastSeenVersion(seenVersion)
      writeLastSeenCache(cacheKey, seenVersion)
      window.dispatchEvent(
        new CustomEvent(RELEASE_SEEN_EVENT, { detail: { version: seenVersion } }),
      )
    } catch (err) {
      console.error('Failed to mark release as seen', err)
    }
  }, [currentVersion, isAuthenticated, lastSeenVersion, cacheKey])

  return {
    currentVersion,
    lastSeenVersion,
    unseen: hasUnseen(currentVersion, lastSeenVersion) && !loading,
    loading,
    markSeen,
  }
}
